import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import {
    createGame, step, WIDTH, HEIGHT, TAKEOFF_SPEED, SERVICE_START, SERVICE_END,
} from '../../lib/arcade/blueMaxEngine';
import { render as renderFrame } from '../../lib/arcade/blueMaxRenderer';
import { createBlueMaxSound } from '../../lib/arcade/blueMaxSound';
import { createGameLoop } from '../../lib/arcade/gameLoop';
import ArcadeLeaderboard from './leaderboard';

export const GAME_KEY = 'blueMax';
const CONTROL_KEYS = {
    ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right',
    ArrowUp: 'up', KeyW: 'up', ArrowDown: 'down', KeyS: 'down', Space: 'fire', KeyB: 'bomb',
};

function hud(game) {
    const p = game.plane;
    return {
        score: game.score, mission: game.mission, engineStatus: game.status,
        fuel: Math.ceil(p.fuel), bombs: Math.floor(p.bombs), condition: Math.ceil(p.condition),
        altitude: Math.round(p.z), takeoffReady: p.speed >= TAKEOFF_SPEED,
        airfield: !game.runwayUsed && p.y >= SERVICE_START - 350 && p.y < SERVICE_END,
        reason: game.reason,
    };
}

/** Blue Max arcade UI (C). Keyboard/pointer input and asynchronous results belong to this mounted run. */
@observer
class BlueMax extends Component {
    constructor(props) {
        super(props);
        this.game = createGame();
        this.state = {
            ...hud(this.game), screen: 'title', scale: 1, muted: !!util.get('arcade.muted'),
            submitting: false, saveFailed: false, loadFailed: false, result: null,
        };
        this.mode = 'title';
        this.keys = new Set();
        this.pointers = new Map();
        this.bombPending = false;
        this.departPending = false;
        this.startKey = null;
        this.unmounted = false;
        this.completedGame = null;
        this.frame = 0;
        this.sound = createBlueMaxSound({ muted: this.state.muted });
        this.loop = createGameLoop({ onTick: this.tick, onFrame: this.afterFrame });
    }

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
        window.addEventListener('pointerup', this.releasePointer);
        window.addEventListener('pointercancel', this.releasePointer);
        window.addEventListener('resize', this.fitCanvas);
        window.addEventListener('blur', this.pause);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.fitCanvas();
        this.draw();
        this.loadScores(this.game);
    }

    componentDidUpdate() {
        this.draw();
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.stop();
        this.sound.dispose();
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('pointerup', this.releasePointer);
        window.removeEventListener('pointercancel', this.releasePointer);
        window.removeEventListener('resize', this.fitCanvas);
        window.removeEventListener('blur', this.pause);
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    current = game => !this.unmounted && this.mode !== 'closed' && this.game === game;

    loadScores = async (game) => {
        const store = this.props.stores.arcadeScoreStore;
        if (!store?.loadLeaderboard) return;
        try {
            const response = await store.loadLeaderboard(GAME_KEY);
            if (this.current(game)) this.setState({ loadFailed: response?.status !== 200 });
        } catch (error) {
            if (this.current(game)) this.setState({ loadFailed: true });
        }
    };

    fitCanvas = () => {
        const width = Math.max(160, (this.wrapRef?.clientWidth || window.innerWidth || WIDTH + 32) - 32);
        const height = Math.max(HEIGHT, (window.innerHeight || 700) - 260);
        const scale = Math.min(width / WIDTH, height / HEIGHT, 2.5);
        if (scale !== this.state.scale) this.setState({ scale });
    };

    draw = () => {
        const ctx = this.canvasRef?.getContext('2d');
        if (ctx) renderFrame(ctx, this.game, this.frame);
    };

    clearInput = () => {
        this.keys.clear();
        this.pointers.clear();
        this.bombPending = false;
        this.departPending = false;
        this.startKey = null;
        this.game.bombHeld = false;
    };

    stop = () => {
        this.loop.stop();
        this.clearInput();
        this.sound.silence();
    };

    startGame = () => {
        this.stop();
        this.sound.unlock();
        this.game = createGame();
        this.completedGame = null;
        this.frame = 0;
        this.mode = 'game';
        this.setState({
            ...hud(this.game), screen: 'game', submitting: false, saveFailed: false, result: null,
        }, () => {
            if (!this.unmounted && this.mode === 'game') this.loop.start();
        });
    };

    pause = () => {
        if (this.mode !== 'game') return;
        this.stop();
        this.mode = 'paused';
        this.setState({ screen: 'paused' });
    };

    resume = () => {
        if (this.mode !== 'paused') return;
        this.sound.unlock();
        this.mode = 'game';
        this.setState({ screen: 'game' }, () => {
            if (!this.unmounted && this.mode === 'game') this.loop.start();
        });
    };

    onVisibility = () => {
        if (document.hidden) this.pause();
    };

    leave = (callback) => {
        this.stop();
        this.mode = 'closed';
        callback?.();
    };

    back = () => this.leave(this.props.onBack);

    close = () => this.leave(() => this.props.stores.appState.toggleDrawer(false, this.props.drawerLevel || 1));

    toggleMute = () => {
        const muted = !this.state.muted;
        this.sound.setMuted(muted);
        if (!muted) this.sound.unlock();
        util.set('arcade.muted', muted);
        this.setState({ muted });
    };

    input = () => {
        const result = {};
        this.keys.forEach(code => { result[CONTROL_KEYS[code]] = true; });
        this.pointers.forEach(control => { result[control] = true; });
        return { ...result, bomb: !!result.bomb || this.bombPending, depart: this.departPending };
    };

    tick = (dtMs) => {
        if (this.mode !== 'game') return;
        step(this.game, this.input(), dtMs);
        this.bombPending = false;
        this.departPending = false;
    };

    afterFrame = () => {
        if (this.mode !== 'game') return;
        this.frame += 1;
        this.game.events.splice(0).forEach(event => this.sound.play(event));
        this.draw();
        const values = hud(this.game);
        if (Object.keys(values).some(key => this.state[key] !== values[key])) this.setState(values);
        if (this.game.status === 'gameOver') this.onGameOver();
    };

    onGameOver = async () => {
        const game = this.game;
        if (this.completedGame === game) return;
        this.completedGame = game;
        this.loop.stop();
        this.clearInput();
        this.mode = 'gameOver';
        this.setState({ ...hud(game), screen: 'gameOver', submitting: game.score > 0 });
        if (game.score <= 0) return;
        const store = this.props.stores.arcadeScoreStore;
        try {
            const response = await store?.submitScore({
                game: GAME_KEY, score: game.score, wave: game.mission, durationMs: Math.floor(game.elapsedMs),
            });
            if (!this.current(game)) return;
            if (response?.status !== 200 && response?.status !== 201) {
                this.setState({ submitting: false, saveFailed: true });
                return;
            }
            this.setState({ submitting: false, result: response.data || null });
            await this.loadScores(game);
        } catch (error) {
            if (this.current(game)) this.setState({ submitting: false, saveFailed: true });
        }
    };

    onKeyDown = (e) => {
        const control = CONTROL_KEYS[e.code];
        if (control || e.code === 'KeyP' || e.code === 'Enter') {
            if (e.target?.tagName === 'BUTTON' && e.code === 'Enter') return;
            e.preventDefault();
        }
        if ((this.mode === 'title' || this.mode === 'gameOver')
            && ['Space', 'Enter'].includes(e.code) && !e.repeat) {
            this.startGame();
            this.startKey = e.code;
        } else if (e.code === 'KeyP' && !e.repeat) {
            if (this.mode === 'game') this.pause();
            else if (this.mode === 'paused') this.resume();
        } else if (this.mode === 'game' && e.code !== this.startKey) {
            if (control) {
                if (control === 'bomb' && !this.keys.has(e.code)) this.bombPending = true;
                this.keys.add(e.code);
            }
            if (e.code === 'Enter' && !e.repeat) this.departPending = true;
        }
    };

    onKeyUp = (e) => {
        this.keys.delete(e.code);
        if (e.code === this.startKey) this.startKey = null;
    };

    pressPointer = control => (e) => {
        e.preventDefault();
        if (this.mode !== 'game') return;
        this.pointers.set(e.pointerId, control);
        if (control === 'bomb') this.bombPending = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    releasePointer = (e) => {
        this.pointers.delete(e.pointerId);
    };

    depart = () => { this.departPending = true; };

    renderControls() {
        const button = (control, content) => (
            <button
                type='button'
                class={`btn ${control === 'bomb' ? 'btn-warning' : 'btn-outline-light'} arcade-touch-btn`}
                data-control={control}
                style='min-width: 42px; min-height: 42px; touch-action: none; user-select: none;'
                onPointerDown={this.pressPointer(control)}
                onPointerUp={this.releasePointer}
                onPointerCancel={this.releasePointer}
                onLostPointerCapture={this.releasePointer}
            >{content}</button>
        );
        return (
            <div class='d-flex flex-wrap justify-content-center gap-2 mt-2'>
                <div class='d-flex gap-2'>
                    {button('left', <span>←<span class='visually-hidden'>
                        <Text id='arcade.blueMaxLeft'>Steer left</Text></span></span>)}
                    {button('up', <span>↑<span class='visually-hidden'>
                        <Text id='arcade.blueMaxUp'>Climb</Text></span></span>)}
                    {button('down', <span>↓<span class='visually-hidden'>
                        <Text id='arcade.blueMaxDown'>Descend</Text></span></span>)}
                    {button('right', <span>→<span class='visually-hidden'>
                        <Text id='arcade.blueMaxRight'>Steer right</Text></span></span>)}
                </div>
                <div class='d-flex gap-2'>
                    {button('fire', <Text id='arcade.fire'>Fire</Text>)}
                    {button('bomb', <Text id='arcade.blueMaxBomb'>Bomb</Text>)}
                </div>
            </div>
        );
    }

    renderPrompt() {
        const { engineStatus, takeoffReady, airfield, fuel } = this.state;
        if (engineStatus === 'ready') return <Text id='arcade.ready'>Ready!</Text>;
        if (engineStatus === 'takeoff') return takeoffReady
            ? <Text id='arcade.blueMaxLiftOff'>Hold ↑ to take off</Text>
            : <Text id='arcade.blueMaxAccelerating'>Accelerating… wait for takeoff speed</Text>;
        if (engineStatus === 'landing') return <Text id='arcade.blueMaxLanding'>Landing…</Text>;
        if (engineStatus === 'servicing') return <Text id='arcade.blueMaxServicing'>Refuelling and repairing…</Text>;
        if (airfield) return <Text id='arcade.blueMaxAirfield'>Airfield ahead: align with the runway and descend</Text>;
        if (fuel <= 20) return <Text id='arcade.blueMaxLowFuel'>Low fuel — land at the next airfield</Text>;
        return <Text id='arcade.blueMaxTargets'>Bomb the marked targets · watch your altitude</Text>;
    }

    renderBack() {
        return this.props.onBack && (
            <button type='button' class='btn btn-outline-light' onClick={this.back}>
                <Text id='arcade.backToArcade'>Back to arcade</Text>
            </button>
        );
    }

    render() {
        const {
            screen, score, mission, scale, fuel, bombs, condition, altitude, muted,
            engineStatus, submitting, saveFailed, loadFailed, result, reason,
        } = this.state;
        const store = this.props.stores.arcadeScoreStore;
        const personalBest = store?.bestByGame?.[GAME_KEY]?.score || 0;
        const best = Math.max(personalBest, result?.best?.score || 0, score);
        const active = screen === 'game' || screen === 'paused';
        const dimensions = `width: ${WIDTH * scale}px; height: ${HEIGHT * scale}px;`;
        return (
            <div class='d-flex flex-column align-items-center p-3 pb-5 bg-black text-white min-vh-100'
                data-bs-theme='dark'
                ref={c => { this.wrapRef = c; }}>
                <div class='w-100' style='max-width: 640px;'>
                    <div class='arcade-hud d-flex flex-wrap justify-content-between gap-2 small mb-1'>
                        <span><Text id='arcade.score'>Score</Text> {score}</span>
                        <span><Text id='arcade.blueMaxMission'>Mission</Text> {mission}</span>
                        <span><Text id='arcade.best'>Best</Text> {best}</span>
                        <button type='button' class='btn btn-sm btn-outline-light py-0' onClick={this.toggleMute}>
                            {muted ? <Text id='arcade.unmute'>Unmute</Text> : <Text id='arcade.mute'>Mute</Text>}
                        </button>
                    </div>
                    <div class='d-flex flex-wrap justify-content-between gap-2 small mb-2'>
                        <span class={fuel <= 20 ? 'text-warning' : ''}>
                            <Text id='arcade.blueMaxFuel'>Fuel</Text> {fuel}%
                        </span>
                        <span><Text id='arcade.blueMaxBombs'>Bombs</Text> {bombs}</span>
                        <span><Text id='arcade.blueMaxAltitude'>Altitude</Text> {altitude}</span>
                        <span><Text id='arcade.blueMaxCondition'>Condition</Text> {condition}/4</span>
                    </div>
                </div>
                <div class='arcade-screen' style={dimensions}>
                    <canvas ref={c => { this.canvasRef = c; }} width={WIDTH} height={HEIGHT}
                        class='arcade-canvas' style={dimensions} aria-label='Blue Max' />
                    {screen === 'title' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.blueMax'>Blue Max</Text></h3>
                            <div class='small'>
                                <Text id='arcade.blueMaxIntro'>Fly, bomb targets, and land to refuel.</Text>
                            </div>
                            <button type='button' class='btn btn-light' onClick={this.startGame}>
                                <Text id='arcade.start'>Start game</Text>
                            </button>
                            {this.renderBack()}
                        </div>
                    )}
                    {screen === 'paused' && (
                        <div class='arcade-overlay'>
                            <div><Text id='arcade.paused'>Paused — press P or Resume to continue</Text></div>
                            <button type='button' class='btn btn-light' onClick={this.resume}>
                                <Text id='arcade.resume'>Resume</Text>
                            </button>
                            {this.renderBack()}
                        </div>
                    )}
                    {screen === 'gameOver' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.gameOver'>Game over</Text></h3>
                            {reason === 'fuel' && <Text id='arcade.blueMaxOutOfFuel'>Out of fuel</Text>}
                            {reason === 'crash' && <Text id='arcade.blueMaxCrashed'>Aircraft crashed</Text>}
                            {reason === 'damage' && <Text id='arcade.blueMaxDestroyed'>Aircraft destroyed</Text>}
                            <div><Text id='arcade.finalScore'>Final score</Text>: {score}</div>
                            {submitting && <div class='small'><Text id='arcade.savingScore'>Saving score…</Text></div>}
                            {saveFailed && <div class='small text-warning'>
                                <Text id='arcade.blueMaxSaveFailed'>Score could not be saved.</Text>
                            </div>}
                            {result?.isNewBest === true && <div class='small'>
                                <Text id='arcade.newBest'>New personal best!</Text>
                            </div>}
                            <button type='button' class='btn btn-light' onClick={this.startGame}>
                                <Text id='arcade.playAgain'>Play again</Text>
                            </button>
                            {this.renderBack()}
                        </div>
                    )}
                    {screen === 'game' && engineStatus === 'missionComplete' && (
                        <div class='arcade-overlay'>
                            <Text id='arcade.blueMaxMissionComplete' fields={{ mission }}>
                                Mission {mission} complete!
                            </Text>
                        </div>
                    )}
                </div>
                {active && (
                    <div class='small text-center mt-2' style='max-width: 480px;' aria-live='polite'>
                        {this.renderPrompt()}
                        {engineStatus === 'servicing' && screen === 'game' && (
                            <button type='button' class='btn btn-sm btn-success ms-2' onClick={this.depart}>
                                <Text id='arcade.blueMaxTakeoff'>Take off</Text>
                            </button>
                        )}
                    </div>
                )}
                {active && this.renderControls()}
                <div class='small text-center text-body-secondary mt-2' style='max-width: 480px;'>
                    <Text id='arcade.controlsBlueMaxKeyboard'>
                        Arrows/WASD steer and change altitude · Space fires · B bombs · P pauses
                    </Text>
                    <div><Text id='arcade.controlsBlueMaxTouch'>Hold arrows and Fire · tap Bomb</Text></div>
                </div>
                <div class='d-flex justify-content-center gap-2 mt-2'>
                    {active && (
                        <button type='button' class='btn btn-outline-light'
                            onClick={screen === 'paused' ? this.resume : this.pause}>
                            {screen === 'paused' ? <Text id='arcade.resume'>Resume</Text>
                                : <Text id='arcade.pause'>Pause</Text>}
                        </button>
                    )}
                    <button type='button' class='btn btn-outline-light' onClick={this.close}>
                        <Text id='arcade.close'>Close</Text>
                    </button>
                </div>
                {loadFailed && <div class='small text-warning mt-2'>
                    <Text id='arcade.blueMaxLoadFailed'>Leaderboard could not be loaded.</Text>
                </div>}
                <div class='w-100' style='max-width: 480px;'>
                    <ArcadeLeaderboard stores={this.props.stores} game={GAME_KEY} />
                </div>
            </div>
        );
    }
}

export default BlueMax;
